import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface AmazonLinks {
  canada: string;
  us: string;
}

interface ProductForm {
  name: string;
  description: string;
  image: string;
  category: string;
  amazonLinks: AmazonLinks;
  shortcutId: string;
}

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState('');
  const [formData, setFormData] = useState<ProductForm>({
    name: '',
    description: '',
    image: '',
    category: '',
    amazonLinks: {
      canada: '',
      us: ''
    },
    shortcutId: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Api-Key': apiKey
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to add product');
      }

      setSuccess('Product added successfully!');
      setFormData({
        name: '',
        description: '',
        image: '',
        category: '',
        amazonLinks: {
          canada: '',
          us: ''
        },
        shortcutId: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleAmazonLinkChange = (region: keyof AmazonLinks, value: string) => {
    setFormData({
      ...formData,
      amazonLinks: {
        ...formData.amazonLinks,
        [region]: value
      }
    });
  };

  return (
    <div className="min-h-screen bg-dark-primary p-8">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-3xl font-bold text-dark-text mb-8">Add New Product</h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-dark-text mb-2">API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
              required
            />
          </div>

          <div>
            <label className="block text-dark-text mb-2">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
              required
            />
          </div>

          <div>
            <label className="block text-dark-text mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
              required
            />
          </div>

          <div>
            <label className="block text-dark-text mb-2">Image URL</label>
            <input
              type="text"
              value={formData.image}
              onChange={(e) => setFormData({ ...formData, image: e.target.value })}
              className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
              required
            />
          </div>

          <div>
            <label className="block text-dark-text mb-2">Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
              required
            />
          </div>

          <div>
            <label className="block text-dark-text mb-2">Shortcut ID</label>
            <input
              type="text"
              value={formData.shortcutId}
              onChange={(e) => setFormData({ ...formData, shortcutId: e.target.value })}
              className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
              required
            />
          </div>

          <div>
            <label className="block text-dark-text mb-2">Amazon Links</label>
            <div className="space-y-4">
              <div>
                <label className="block text-dark-text-secondary mb-1">Canada</label>
                <input
                  type="text"
                  value={formData.amazonLinks.canada}
                  onChange={(e) => handleAmazonLinkChange('canada', e.target.value)}
                  className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
                  required
                  placeholder="https://amzn.to/..."
                />
              </div>
              <div>
                <label className="block text-dark-text-secondary mb-1">US</label>
                <input
                  type="text"
                  value={formData.amazonLinks.us}
                  onChange={(e) => handleAmazonLinkChange('us', e.target.value)}
                  className="w-full p-2 rounded bg-dark-secondary text-dark-text border border-dark-accent-pink"
                  required
                  placeholder="https://amzn.to/..."
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="text-red-500 bg-red-100 p-3 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="text-green-500 bg-green-100 p-3 rounded">
              {success}
            </div>
          )}

          <button
            type="submit"
            className="w-full px-4 py-2 bg-dark-accent-pink text-white rounded hover:bg-dark-accent-purple"
          >
            Add Product
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddProduct; 